import React, { useState, useRef, useEffect } from 'react';
import { FiMoreVertical, FiEdit2, FiTrash2, FiExternalLink, FiLock } from 'react-icons/fi';

interface ActionMenuProps {
  link: string;
  project: { id: number | string; name: string; desc: string; users: any[]; type?: string };
  onEdit: () => void;
  onDelete: (project: any) => void;
}

const ProjectActionMenu: React.FC<ActionMenuProps> = ({
  link,
  project,
  onEdit,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if project is a Free Trial project (should not be editable)
  const isFreeTrialProject = project.type === "FREE TRIAL";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMenuButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up to parent row
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleMenuButtonClick}
        className="p-2 hover:bg-white/[0.08] rounded-lg"
      >
        <FiMoreVertical className="text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-[#1A1A1A] rounded-lg border border-white/[0.08] shadow-lg z-50">
          {/* Only show Edit option if project is NOT a Free Trial */}
          {!isFreeTrialProject && onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up to parent row
                onEdit();
                setIsOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-400 hover:bg-white/[0.08] hover:text-white"
            >
              <FiEdit2 />
              Edit Project
            </button>
          )}
          
          {/* Always show View Project if link exists */}
          {link && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up to parent row
                window.open(link, '_blank');
                setIsOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-400 hover:bg-white/[0.08] hover:text-white"
            >
              <FiExternalLink />
              View Project
            </button>
          )}
          
          {/* Only show Delete option if project is NOT a Free Trial */}
          {!isFreeTrialProject && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up to parent row
                onDelete(project);
                setIsOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-white/[0.08] hover:text-red-300"
            >
              <FiTrash2 />
              Delete Project
            </button>
          )}
          
          {/* Only show Block option if project is NOT a Free Trial */}
          {!isFreeTrialProject && (
            <button
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-400 hover:bg-white/[0.08] hover:text-white"
            >
              <FiLock />
              Block Project
            </button>
          )}
          
          {/* Show message for Free Trial projects */}
          {isFreeTrialProject && (
            <div className="px-4 py-2 text-xs text-left text-gray-500 border-t border-white/[0.08]">
              Demo projects cannot <br /> be modified. 
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectActionMenu; 